import { writeInstanceIdsParam } from '../../../hooks/useChatUrlInstanceIds';
import type { ControlSelections } from '../../types/controls';
import type { TemplateControls } from '../templateControls';

/** e.g. `?time_range=3mo`. */
export const TIME_RANGE_PARAM = 'time_range';

/** Present means proposed, not applied. Absent — every URL until now — means it describes the view. */
export const PREFILL_PARAM = 'prefill';

/** e.g. `?f.platform=RING`. Prefixed so a control key cannot collide with an app-level param. */
export const CONTROL_PARAM_PREFIX = 'f.';

/** The filters a dashboard is rendered with, as stored in the URL. */
export type AppliedDashboardFilters = {
  selectionsByControlKey: ControlSelections;
  /** Empty means every accessible instance — the same convention as the chat's `instance_ids`. */
  scopedInstanceIds: string[];
  timeRangeOverride: string | undefined;
  /** False while the criteria are only proposed, which is what holds the render back. */
  isApplied: boolean;
};

/** Values are comma-separated, so each is escaped: a customer is a free-text account name and
 * "Scality, Inc." would otherwise read back as two. Malformed escapes keep the raw value. */
const decodeValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const writeControlParam = (next: URLSearchParams, controlKey: string, values: string[]): void => {
  next.set(CONTROL_PARAM_PREFIX + controlKey, values.map(encodeURIComponent).join(','));
};

export const readControlSelections = (searchParams: URLSearchParams): ControlSelections => {
  const selections: ControlSelections = {};
  for (const [name, value] of searchParams.entries()) {
    if (!name.startsWith(CONTROL_PARAM_PREFIX)) continue;
    const values = value.split(',').filter(Boolean).map(decodeValue);
    if (values.length > 0) selections[name.slice(CONTROL_PARAM_PREFIX.length)] = values;
  }
  return selections;
};

/** The instance scope rides `instance_ids`, so it is never one of the control params. */
export const withoutInstanceScope = (
  selections: ControlSelections,
  instanceControlKey: string | undefined,
): ControlSelections => {
  if (!instanceControlKey) return selections;
  const { [instanceControlKey]: _scope, ...rest } = selections;
  return rest;
};

const isDashboardOnlyParam = (name: string): boolean =>
  name === TIME_RANGE_PARAM || name === PREFILL_PARAM || name.startsWith(CONTROL_PARAM_PREFIX);

/** Conversation mode has no controls, so only what both modes share survives: `instance_ids`. */
export const searchOutsideDashboardMode = (search: string): string => {
  const kept = new URLSearchParams(search);
  for (const name of [...kept.keys()]) {
    if (isDashboardOnlyParam(name)) kept.delete(name);
  }
  const value = kept.toString();
  return value ? `?${value}` : '';
};

export const markApplied = (next: URLSearchParams): URLSearchParams => {
  next.delete(PREFILL_PARAM);
  return next;
};

export const markPrefill = (next: URLSearchParams): URLSearchParams => {
  next.set(PREFILL_PARAM, '1');
  return next;
};

const replaceControlParams = (
  next: URLSearchParams,
  selections: ControlSelections,
  { instanceControlKey, controlKeys }: TemplateControls,
): void => {
  for (const name of [...next.keys()]) {
    if (name.startsWith(CONTROL_PARAM_PREFIX)) next.delete(name);
  }
  for (const [controlKey, values] of Object.entries(selections)) {
    if (controlKey === instanceControlKey || values.length === 0 || !controlKeys.includes(controlKey)) continue;
    writeControlParam(next, controlKey, values);
  }
};

/** Wholesale, so a control that lost its value — or belongs to another template — leaves none. */
export const replaceFilterParams = (
  next: URLSearchParams,
  selections: ControlSelections,
  timeRange: string | undefined,
  template: TemplateControls,
): URLSearchParams => {
  replaceControlParams(next, selections, template);
  if (template.instanceControlKey) {
    writeInstanceIdsParam(next, selections[template.instanceControlKey] ?? []);
  }
  if (timeRange) {
    next.set(TIME_RANGE_PARAM, timeRange);
  } else {
    next.delete(TIME_RANGE_PARAM);
  }
  return next;
};
